/**
 * Admin — Waitlist Tab (DEV-144)
 *
 * Displays pending registration waitlist entries with approve/deny actions,
 * batch approve, and status filtering. English only — system admin UI.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchWaitlist,
  fetchWaitlistCounts,
  approveWaitlistEntry,
  denyWaitlistEntry,
  batchApproveWaitlist,
  type WaitlistEntry,
  type WaitlistCounts,
} from '../../api/waitlist';
import styles from './AdminWaitlist.module.css';

// ── Helpers ──────────────────────────────────────────────────

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
}

// ── Component ────────────────────────────────────────────────

type StatusTab = 'pending' | 'approved' | 'denied' | 'all';

export default function AdminWaitlist() {
  const [statusTab, setStatusTab] = useState<StatusTab>('pending');
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [counts, setCounts] = useState<WaitlistCounts>({});
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [actionInProgress, setActionInProgress] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, countsData] = await Promise.all([
        fetchWaitlist(statusTab, page, 50),
        fetchWaitlistCounts(),
      ]);
      setEntries(data.entries);
      setTotalCount(data.totalCount);
      setTotalPages(data.totalPages);
      setCounts(countsData);
    } catch (err) {
      console.error('Failed to load waitlist:', err);
    } finally {
      setLoading(false);
    }
  }, [statusTab, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset page when changing tab
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [statusTab]);

  // ── Actions ──

  const handleApprove = async (id: string) => {
    setActionInProgress(true);
    try {
      await approveWaitlistEntry(id);
      await load();
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      console.error('Failed to approve:', err);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDeny = async (id: string) => {
    setActionInProgress(true);
    try {
      await denyWaitlistEntry(id);
      await load();
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      console.error('Failed to deny:', err);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleBatchApprove = async () => {
    if (selected.size === 0) return;
    setActionInProgress(true);
    try {
      await batchApproveWaitlist(Array.from(selected));
      setSelected(new Set());
      await load();
    } catch (err) {
      console.error('Failed to batch approve:', err);
    } finally {
      setActionInProgress(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === entries.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(entries.map((e) => e.id)));
    }
  };

  const pendingCount = counts.pending ?? 0;

  return (
    <div className={styles.container}>
      {/* ── Status tabs ── */}
      <div className={styles.tabs}>
        {(['pending', 'approved', 'denied', 'all'] as StatusTab[]).map((tab) => (
          <button
            key={tab}
            className={`${styles.tab} ${statusTab === tab ? styles.tabActive : ''}`}
            onClick={() => setStatusTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab !== 'all' && counts[tab] !== undefined && (
              <span className={styles.badge}>{counts[tab]}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Batch actions bar ── */}
      {statusTab === 'pending' && pendingCount > 0 && (
        <div className={styles.batchBar}>
          <span className={styles.batchText}>
            {selected.size > 0
              ? `${selected.size} selected`
              : `${pendingCount} pending request${pendingCount !== 1 ? 's' : ''}`}
          </span>
          {selected.size > 0 && (
            <button
              className={styles.batchApproveBtn}
              onClick={handleBatchApprove}
              disabled={actionInProgress}
            >
              Approve Selected ({selected.size})
            </button>
          )}
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div className={styles.loading}>Loading...</div>
      ) : entries.length === 0 ? (
        <div className={styles.empty}>
          {statusTab === 'pending'
            ? 'No pending waitlist requests.'
            : `No ${statusTab === 'all' ? '' : statusTab + ' '}entries found.`}
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              {statusTab === 'pending' && (
                <th className={styles.thCheck}>
                  <input
                    type="checkbox"
                    checked={selected.size === entries.length && entries.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
              )}
              <th>Email</th>
              <th>Name</th>
              <th>Source</th>
              <th>Requested</th>
              {statusTab !== 'pending' && <th>Status</th>}
              {statusTab !== 'pending' && <th>Reviewed</th>}
              {statusTab === 'pending' && <th className={styles.thActions}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id} className={styles.row}>
                {statusTab === 'pending' && (
                  <td className={styles.tdCheck}>
                    <input
                      type="checkbox"
                      checked={selected.has(entry.id)}
                      onChange={() => toggleSelect(entry.id)}
                    />
                  </td>
                )}
                <td className={styles.emailCell}>{entry.email}</td>
                <td>{entry.displayName || '—'}</td>
                <td className={styles.sourceCell}>{entry.source}</td>
                <td className={styles.dateCell} title={formatDateTime(entry.createdAt)}>
                  {timeAgo(entry.createdAt)}
                </td>
                {statusTab !== 'pending' && (
                  <td>
                    <span
                      className={`${styles.statusBadge} ${
                        entry.status === 'approved'
                          ? styles.statusApproved
                          : entry.status === 'denied'
                          ? styles.statusDenied
                          : styles.statusPending
                      }`}
                    >
                      {entry.status}
                    </span>
                  </td>
                )}
                {statusTab !== 'pending' && (
                  <td className={styles.dateCell}>{formatDate(entry.reviewedAt)}</td>
                )}
                {statusTab === 'pending' && (
                  <td className={styles.actionsCell}>
                    <button
                      className={styles.approveBtn}
                      onClick={() => handleApprove(entry.id)}
                      disabled={actionInProgress}
                      title="Approve — add to allow-list"
                    >
                      Approve
                    </button>
                    <button
                      className={styles.denyBtn}
                      onClick={() => handleDeny(entry.id)}
                      disabled={actionInProgress}
                      title="Deny request"
                    >
                      Deny
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page} of {totalPages} ({totalCount} total)
          </span>
          <button
            className={styles.pageBtn}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
